import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Car, ChevronDown, ChevronLeft, ChevronRight, FileText, ImageIcon, Loader2, MessageSquare, Link2, Search } from "lucide-react";
import {
  Badge,
  Button,
  ConfirmDialog,
  Toast,
  ToastType,
} from "@/components/ui";
import { VehicleFilters, VehicleFiltersData } from "@/components/filters";
import { apiService } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCatalog } from "@/hooks/useCatalog";
import { useVehicleOptions } from "@/hooks/useVehicleOptions";
import type { Lead, Vehicle } from "@/types";
import type { VehicleFiltersAPI } from "@/types";
import { formatCurrency } from "@/utils/formatters";
import { buildOutboundTextPayload } from "@/modules/whatsapp/utils/messageBubbleText";
import { VehicleLinkDialog } from "./VehicleLinkDialog";

const EMPTY_FILTERS: VehicleFiltersData = {
  search: "",
  status: "in_stock",
  condition: "",
  category: "B",
  brandId: "",
  modelId: "",
  versionId: "",
  minYear: "",
  maxYear: "",
  minMileage: "",
  maxMileage: "",
  fuelType: "",
  plateEnd: "",
  armorLevel: "",
  color: "",
  type: "",
  doors: "",
  isAuction: "",
  specialCategory: "",
  characteristics: [],
};

const LEAD_STATUS_OPTIONS: string[] = [
  "new",
  "contacted",
  "qualified",
  "proposal",
  "negotiation",
  "converted",
  "lost",
];

function vehicleTitle(v: Vehicle): string {
  if (v.name?.trim()) return v.name.trim();
  if (v.catalog?.brand?.name && v.catalog?.model?.name) {
    const y = v.catalog.modelYear ?? v.year ?? "";
    return `${v.catalog.brand.name} ${v.catalog.model.name} ${y}`.trim();
  }
  const brand = typeof v.brand === "object" && v.brand?.name ? v.brand.name : "";
  const model = typeof v.model === "object" && v.model?.name ? v.model.name : "";
  const year = v.year ?? v.modelYear ?? "";
  const base = `${brand} ${model} ${year}`.trim();
  return base || v.id;
}

function buildVehicleDescriptionLines(v: Vehicle): string[] {
  const lines: string[] = [];
  const title = vehicleTitle(v);
  if (title) lines.push(title);
  const price =
    typeof v.salePrice === "string"
      ? parseFloat(v.salePrice)
      : typeof v.salePrice === "number"
        ? v.salePrice
        : typeof v.price === "number"
          ? v.price
          : 0;
  if (price > 0) lines.push(formatCurrency(price));
  const mileage = v.mileage ?? v.specifications?.mileage;
  if (mileage != null) lines.push(`${Number(mileage).toLocaleString()} km`);
  const plate = v.identification?.plate || v.identification?.plateEnd;
  if (plate) lines.push(String(plate));
  if (v.status) lines.push(String(v.status));
  return lines;
}

function filtersToApiParams(filters: VehicleFiltersData): VehicleFiltersAPI {
  const params: VehicleFiltersAPI = { page: 1, limit: 20 };
  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.condition) params.condition = filters.condition;
  if (filters.category) params.category = filters.category;
  if (filters.brandId) params.brandId = filters.brandId;
  if (filters.modelId) params.modelId = filters.modelId;
  if (filters.versionId) params.versionId = filters.versionId;
  if (filters.minYear) params.minYear = parseInt(filters.minYear, 10);
  if (filters.maxYear) params.maxYear = parseInt(filters.maxYear, 10);
  if (filters.minMileage) params.minMileage = parseInt(filters.minMileage, 10);
  if (filters.maxMileage) params.maxMileage = parseInt(filters.maxMileage, 10);
  if (filters.fuelType) params.fuelType = filters.fuelType;
  if (filters.plateEnd) params.plateEnd = filters.plateEnd;
  if (filters.armorLevel) params.armorLevel = filters.armorLevel;
  if (filters.color) params.color = filters.color;
  if (filters.type) params.type = filters.type;
  if (filters.doors) params.doors = parseInt(filters.doors, 10);
  if (filters.isAuction) params.isAuction = filters.isAuction === "true";
  if (filters.specialCategory) params.specialCategory = filters.specialCategory;
  if (filters.characteristics.length > 0) params.characteristics = filters.characteristics;
  return params;
}

type DetailVehicle = Vehicle & {
  documents?: Array<{ id: string; name: string; url: string; type?: string }>;
  media?: {
    photos?: Array<Record<string, unknown>>;
    documents?: Array<Record<string, unknown>>;
  };
};

function collectPhotoUrlsFromDetail(detail: DetailVehicle): string[] {
  const direct = (detail.photos || [])
    .map((p) => p.url)
    .filter((u): u is string => typeof u === "string" && u.length > 0);
  if (direct.length > 0) return direct;
  const raw = detail.media?.photos;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item.url === "string" ? item.url : ""))
    .filter((u) => u.length > 0);
}

function collectDocumentsFromDetail(detail: DetailVehicle): Array<{ name: string; url: string }> {
  if (detail.documents?.length) {
    return detail.documents.map((d) => ({ name: d.name, url: d.url })).filter((d) => d.url);
  }
  const raw = detail.media?.documents;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      name: typeof item.name === "string" ? item.name : "document",
      url: typeof item.url === "string" ? item.url : "",
    }))
    .filter((d) => d.url.length > 0);
}

export interface ConversationRightPanelProps {
  phoneNumber: string | null;
  leadId?: string;
  onLeadOrVehicleUpdated?: () => void;
}

export const ConversationRightPanel: React.FC<ConversationRightPanelProps> = ({
  phoneNumber,
  leadId,
  onLeadOrVehicleUpdated,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { brands, models, versions, fetchBrands, fetchModels, fetchVersions } = useCatalog();
  const { options } = useVehicleOptions();

  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [phoneNumberId, setPhoneNumberId] = useState<string | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [leadLoading, setLeadLoading] = useState(false);

  const [filters, setFilters] = useState<VehicleFiltersData>(EMPTY_FILTERS);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehicleResultTotal, setVehicleResultTotal] = useState(0);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);

  const [sendingText, setSendingText] = useState(false);
  const [sendingPhotos, setSendingPhotos] = useState(false);
  const [sendingDocs, setSendingDocs] = useState(false);

  const [confirmPhotosOpen, setConfirmPhotosOpen] = useState(false);
  const [confirmDocsOpen, setConfirmDocsOpen] = useState(false);
  const [showLinkVehicle, setShowLinkVehicle] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [isLeadSectionCollapsed, setIsLeadSectionCollapsed] = useState(false);
  const [isFiltersSectionCollapsed, setIsFiltersSectionCollapsed] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [leadDraft, setLeadDraft] = useState({
    name: "",
    email: "",
    phone: "",
    status: "new" as Lead["status"],
    notes: "",
    condition: "",
    fuelType: "",
    transmission: "",
    minPrice: "",
    maxPrice: "",
    minYear: "",
    maxYear: "",
  });

  const selectedVehicle = useMemo(
    () => vehicles.find((x) => x.id === selectedVehicleId) ?? null,
    [vehicles, selectedVehicleId],
  );

  useEffect(() => {
    if (!phoneNumber) {
      setPhoneNumberId(null);
      return;
    }
    void (async () => {
      try {
        const result = await apiService.getWhatsAppConversation(phoneNumber);
        if (result.phoneNumberId) {
          setPhoneNumberId(result.phoneNumberId);
        }
      } catch {
        // Ignore error, phoneNumberId will remain null
      }
    })();
  }, [phoneNumber]);

  useEffect(() => {
    const category = filters.category || "B";
    void fetchBrands(category);
  }, [filters.category, fetchBrands]);

  useEffect(() => {
    if (filters.brandId) void fetchModels(filters.brandId);
  }, [filters.brandId, fetchModels]);

  useEffect(() => {
    if (filters.modelId) void fetchVersions(filters.modelId);
  }, [filters.modelId, fetchVersions]);

  useEffect(() => {
    if (!leadId || !phoneNumber) {
      setLead(null);
      setIsEditingLead(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLeadLoading(true);
      try {
        const data = await apiService.getLead(leadId);
        if (!cancelled) {
          const nextLead = data as Lead;
          const leadPreferences = (nextLead.preferences || {}) as Record<string, unknown>;
          setLead(nextLead);
          setLeadDraft({
            name: nextLead.name || "",
            email: nextLead.email || "",
            phone: nextLead.phone || "",
            status: (nextLead.status || "new") as Lead["status"],
            notes: nextLead.notes || "",
            condition: nextLead.preferences?.condition || nextLead.interest?.condition || "",
            fuelType: nextLead.preferences?.fuelType || nextLead.interest?.fuelType || "",
            transmission: nextLead.preferences?.transmission || nextLead.interest?.transmission || "",
            minPrice: leadPreferences.priceMin !== undefined ? String(leadPreferences.priceMin) : "",
            maxPrice: leadPreferences.priceMax !== undefined ? String(leadPreferences.priceMax) : "",
            minYear: leadPreferences.yearMin !== undefined ? String(leadPreferences.yearMin) : "",
            maxYear: leadPreferences.yearMax !== undefined ? String(leadPreferences.yearMax) : "",
          });
        }
      } catch {
        if (!cancelled) {
          setLead(null);
          setToast({ message: t("whatsapp.inbox.rightPanel.errors.loadLead"), type: "error" });
        }
      } finally {
        if (!cancelled) setLeadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, phoneNumber, t]);

  const runVehicleSearch = useCallback(async () => {
    if (!phoneNumber) return;
    setVehiclesLoading(true);
    try {
      const res = await apiService.getVehicles(filtersToApiParams(filters));
      const list = res.vehicles || [];
      setVehicles(list);
      setVehicleResultTotal(res.pagination?.total ?? list.length);
    } catch {
      setVehicles([]);
      setVehicleResultTotal(0);
      setToast({ message: t("whatsapp.inbox.rightPanel.errors.loadVehicles"), type: "error" });
    } finally {
      setVehiclesLoading(false);
    }
  }, [filters, phoneNumber, t]);

  useEffect(() => {
    if (!phoneNumber) {
      setVehicles([]);
      setVehicleResultTotal(0);
      setSelectedVehicleId(null);
      return;
    }
    setFilters(EMPTY_FILTERS);
    setSelectedVehicleId(null);
    let cancelled = false;
    void (async () => {
      setVehiclesLoading(true);
      try {
        const res = await apiService.getVehicles(filtersToApiParams(EMPTY_FILTERS));
        if (cancelled) return;
        const list = res.vehicles || [];
        setVehicles(list);
        setVehicleResultTotal(res.pagination?.total ?? list.length);
      } catch {
        if (!cancelled) {
          setVehicles([]);
          setVehicleResultTotal(0);
          setToast({ message: t("whatsapp.inbox.rightPanel.errors.loadVehicles"), type: "error" });
        }
      } finally {
        if (!cancelled) setVehiclesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phoneNumber, t]);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    (Object.entries(filters) as [string, string | string[]][]).forEach(([key, value]) => {
      if (key === "search") return;
      if (Array.isArray(value) && value.length > 0) n += 1;
      else if (typeof value === "string" && value && key !== "category") n += 1;
    });
    return n;
  }, [filters]);

  const sendVehicleText = async () => {
    if (!phoneNumber || !selectedVehicle || !user || !phoneNumberId) return;
    setSendingText(true);
    try {
      const operatorLabel = (user.name || user.email || "").trim();
      const body = buildOutboundTextPayload(operatorLabel, buildVehicleDescriptionLines(selectedVehicle).join("\n"));
      await apiService.sendWhatsAppTextMessage({
        phoneNumberId,
        to: phoneNumber,
        message: body,
        leadId,
      });
      setToast({ message: t("whatsapp.inbox.rightPanel.success.textSent"), type: "success" });
    } catch {
      setToast({ message: t("whatsapp.inbox.rightPanel.errors.sendFailed"), type: "error" });
    } finally {
      setSendingText(false);
    }
  };

  const persistLeadUpdate = async (extraVehicleId?: string) => {
    if (!leadId || !lead) return;
    try {
      setSavingLead(true);

      const preferences = {
        ...(lead.preferences || {}),
        condition: leadDraft.condition || undefined,
        fuelType: leadDraft.fuelType || undefined,
        transmission: leadDraft.transmission || undefined,
        priceMin: leadDraft.minPrice ? Number(leadDraft.minPrice) : undefined,
        priceMax: leadDraft.maxPrice ? Number(leadDraft.maxPrice) : undefined,
        yearMin: leadDraft.minYear ? Number(leadDraft.minYear) : undefined,
        yearMax: leadDraft.maxYear ? Number(leadDraft.maxYear) : undefined,
      };

      const currentVehicleInterests = lead.vehicleInterests
        ? [...lead.vehicleInterests]
        : lead.vehicleInterest?.map((item) => ({
            vehicleId: item.vehicleId,
            priority: item.priority,
            notes: item.notes || "",
          })) || [];

      let nextVehicleInterests = currentVehicleInterests;
      if (extraVehicleId && !currentVehicleInterests.some((v) => v.vehicleId === extraVehicleId)) {
        nextVehicleInterests = [
          ...currentVehicleInterests,
          {
            vehicleId: extraVehicleId,
            priority: currentVehicleInterests.length + 1,
            notes: "",
          },
        ];
      }

      const phoneDigits = leadDraft.phone.replace(/\D/g, "");
      const normalizedPhone =
        phoneDigits.length >= 10
          ? {
              ddi: "+55",
              ddd: phoneDigits.slice(0, 2),
              phone: phoneDigits.slice(2, 11),
              type: "personal",
              isPrimary: true,
              isWhatsApp: true,
            }
          : null;

      const normalizedEmail = leadDraft.email.trim();

      await apiService.updateLead(leadId, {
        name: leadDraft.name.trim(),
        emails: normalizedEmail
          ? [{ email: normalizedEmail, type: "personal", isPrimary: true }]
          : [],
        phones: normalizedPhone ? [normalizedPhone] : [],
        status: leadDraft.status,
        notes: leadDraft.notes.trim() || null,
        preferences,
        vehicleInterests: nextVehicleInterests,
      });

      const refreshed = (await apiService.getLead(leadId)) as Lead;
      const refreshedPreferences = (refreshed.preferences || {}) as Record<string, unknown>;
      setLead(refreshed);
      setLeadDraft({
        name: refreshed.name || "",
        email: refreshed.email || "",
        phone: refreshed.phone || "",
        status: (refreshed.status || "new") as Lead["status"],
        notes: refreshed.notes || "",
        condition: refreshed.preferences?.condition || refreshed.interest?.condition || "",
        fuelType: refreshed.preferences?.fuelType || refreshed.interest?.fuelType || "",
        transmission: refreshed.preferences?.transmission || refreshed.interest?.transmission || "",
        minPrice: refreshedPreferences.priceMin !== undefined ? String(refreshedPreferences.priceMin) : "",
        maxPrice: refreshedPreferences.priceMax !== undefined ? String(refreshedPreferences.priceMax) : "",
        minYear: refreshedPreferences.yearMin !== undefined ? String(refreshedPreferences.yearMin) : "",
        maxYear: refreshedPreferences.yearMax !== undefined ? String(refreshedPreferences.yearMax) : "",
      });
      setIsEditingLead(false);
      setToast({ message: t("conversations.leadData.updated"), type: "success" });
      onLeadOrVehicleUpdated?.();
    } catch {
      setToast({ message: t("conversations.leadData.updateError"), type: "error" });
    } finally {
      setSavingLead(false);
    }
  };

  const updateLeadStatusQuick = async (status: Lead["status"]) => {
    if (!leadId) return;
    try {
      setIsUpdatingStatus(true);
      await apiService.updateLead(leadId, { status });
      const refreshed = (await apiService.getLead(leadId)) as Lead;
      setLead(refreshed);
      setLeadDraft((prev) => ({ ...prev, status: (refreshed.status || "new") as Lead["status"] }));
      setToast({ message: t("conversations.leadData.updated"), type: "success" });
      onLeadOrVehicleUpdated?.();
    } catch {
      setToast({ message: t("conversations.leadData.updateError"), type: "error" });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const runSendPhotos = async () => {
    if (!phoneNumber || !selectedVehicleId || !phoneNumberId) return;
    setSendingPhotos(true);
    setConfirmPhotosOpen(false);
    try {
      const raw = await apiService.getVehicleById(selectedVehicleId);
      const detail = raw as DetailVehicle;
      const urls = collectPhotoUrlsFromDetail(detail).slice(0, 10);
      if (urls.length === 0) {
        setToast({ message: t("whatsapp.inbox.rightPanel.errors.noPhotos"), type: "warning" });
        return;
      }
      let ok = 0;
      for (let i = 0; i < urls.length; i += 1) {
        try {
          await apiService.sendWhatsAppMediaMessage({
            phoneNumberId,
            to: phoneNumber,
            mediaType: "image",
            mediaUrl: urls[i],
            caption: i === 0 ? vehicleTitle(detail) : undefined,
            leadId,
          });
          ok += 1;
          await new Promise((r) => setTimeout(r, 450));
        } catch {
          break;
        }
      }
      setToast({
        message:
          ok === urls.length
            ? t("whatsapp.inbox.rightPanel.success.photosSent", { count: ok })
            : t("whatsapp.inbox.rightPanel.success.photosPartial", { ok, total: urls.length }),
        type: ok > 0 ? "success" : "error",
      });
    } catch {
      setToast({ message: t("whatsapp.inbox.rightPanel.errors.sendFailed"), type: "error" });
    } finally {
      setSendingPhotos(false);
    }
  };

  const runSendDocuments = async () => {
    if (!phoneNumber || !selectedVehicleId || !phoneNumberId) return;
    setSendingDocs(true);
    setConfirmDocsOpen(false);
    try {
      const raw = await apiService.getVehicleById(selectedVehicleId);
      const detail = raw as DetailVehicle;
      const docs = collectDocumentsFromDetail(detail);
      if (docs.length === 0) {
        setToast({ message: t("whatsapp.inbox.rightPanel.errors.noDocuments"), type: "warning" });
        return;
      }
      let ok = 0;
      for (let i = 0; i < docs.length; i += 1) {
        const doc = docs[i];
        if (!doc.url) continue;
        try {
          await apiService.sendWhatsAppMediaMessage({
            phoneNumberId,
            to: phoneNumber,
            mediaType: "document",
            mediaUrl: doc.url,
            filename: doc.name || `document-${i + 1}`,
            leadId,
          });
          ok += 1;
          await new Promise((r) => setTimeout(r, 450));
        } catch {
          break;
        }
      }
      setToast({
        message:
          ok === docs.length
            ? t("whatsapp.inbox.rightPanel.success.docsSent", { count: ok })
            : t("whatsapp.inbox.rightPanel.success.docsPartial", { ok, total: docs.length }),
        type: ok > 0 ? "success" : "error",
      });
    } catch {
      setToast({ message: t("whatsapp.inbox.rightPanel.errors.sendFailed"), type: "error" });
    } finally {
      setSendingDocs(false);
    }
  };

  const handleVehicleLinked = (_vehicle: Vehicle) => {
    setToast({ message: t("conversations.vehicleLink.linkSuccess"), type: "success" });
    onLeadOrVehicleUpdated?.();
  };

  if (!phoneNumber) {
    return (
      <div className="w-[min(100%,380px)] border-l border-border bg-card flex flex-col items-center justify-center p-6 text-center flex-shrink-0">
        <p className="text-sm text-muted-foreground">{t("whatsapp.inbox.rightPanel.empty")}</p>
      </div>
    );
  }

  return (
    <div
      className={`border-l border-border bg-card min-h-0 flex-shrink-0 transition-all duration-200 ${
        isPanelCollapsed ? "w-12" : "w-[min(100%,380px)] flex flex-col"
      }`}
    >
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {leadId && (
        <VehicleLinkDialog
          open={showLinkVehicle}
          onOpenChange={setShowLinkVehicle}
          leadId={leadId}
          leadName={lead?.name || ""}
          onVehicleLinked={handleVehicleLinked}
        />
      )}

      <ConfirmDialog
        isOpen={confirmPhotosOpen}
        onClose={() => setConfirmPhotosOpen(false)}
        onConfirm={() => void runSendPhotos()}
        title={t("whatsapp.inbox.rightPanel.confirmPhotosTitle")}
        description={t("whatsapp.inbox.rightPanel.confirmPhotosBody")}
        confirmText={t("whatsapp.inbox.rightPanel.confirmSend")}
        type="info"
        isLoading={sendingPhotos}
      />

      <ConfirmDialog
        isOpen={confirmDocsOpen}
        onClose={() => setConfirmDocsOpen(false)}
        onConfirm={() => void runSendDocuments()}
        title={t("whatsapp.inbox.rightPanel.confirmDocsTitle")}
        description={t("whatsapp.inbox.rightPanel.confirmDocsBody")}
        confirmText={t("whatsapp.inbox.rightPanel.confirmSend")}
        type="info"
        isLoading={sendingDocs}
      />

      {isPanelCollapsed ? (
        <div className="h-full flex items-start justify-center pt-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsPanelCollapsed(false)}
          >
            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ) : (
        <>
          <div className="p-3 border-b border-border">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground stroke-[1.5]" />
                {t("whatsapp.inbox.rightPanel.leadSection")}
              </h2>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsLeadSectionCollapsed((prev) => !prev)}
                  aria-label={
                    isLeadSectionCollapsed
                      ? t("whatsapp.inbox.rightPanel.expandContactSection")
                      : t("whatsapp.inbox.rightPanel.collapseContactSection")
                  }
                  title={
                    isLeadSectionCollapsed
                      ? t("whatsapp.inbox.rightPanel.expandContactSection")
                      : t("whatsapp.inbox.rightPanel.collapseContactSection")
                  }
                >
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      isLeadSectionCollapsed ? "-rotate-90" : "rotate-0"
                    }`}
                  />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsPanelCollapsed(true)}
                >
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
            {isLeadSectionCollapsed ? null : leadLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : lead ? (
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t("conversations.leadData.name")}</p>
                <p className="text-foreground font-medium break-words">{lead.name}</p>
              </div>
              {lead.phone && (
                <div>
                  <p className="text-xs text-muted-foreground">{t("conversations.leadData.phone")}</p>
                  <p className="text-foreground">{lead.phone}</p>
                </div>
              )}
              {lead.email && (
                <div>
                  <p className="text-xs text-muted-foreground">{t("conversations.leadData.email")}</p>
                  <p className="text-foreground break-all">{lead.email}</p>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="gray" size="sm">
                  {lead.status}
                </Badge>
                {lead.assignedUser?.name && (
                  <span className="text-xs text-muted-foreground">{lead.assignedUser.name}</span>
                )}
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <select
                  value={leadDraft.status}
                  onChange={(e) =>
                    setLeadDraft((prev) => ({ ...prev, status: e.target.value as Lead["status"] }))
                  }
                  className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                >
                  {LEAD_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isUpdatingStatus}
                  onClick={() => void updateLeadStatusQuick(leadDraft.status)}
                >
                  {isUpdatingStatus ? "..." : "Aplicar"}
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-1"
                onClick={() => setIsEditingLead((prev) => !prev)}
              >
                {isEditingLead ? t("conversations.leadData.cancelEdit") : t("conversations.leadData.editLead")}
              </Button>
              {leadId && (
                <Button variant="outline" size="sm" className="w-full gap-2 mt-2" onClick={() => setShowLinkVehicle(true)}>
                  <Link2 className="h-4 w-4" />
                  {t("conversations.quickActions.linkVehicle")}
                </Button>
              )}
              {selectedVehicleId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-1"
                  disabled={savingLead}
                  onClick={() => void persistLeadUpdate(selectedVehicleId)}
                >
                  {t("conversations.quickActions.addSelectedVehicleInterest")}
                </Button>
              )}
              {isEditingLead && (
                <div className="mt-2 rounded-lg border border-border p-2 space-y-2">
                  <input
                    value={leadDraft.name}
                    onChange={(e) => setLeadDraft((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                    placeholder={t("conversations.leadData.name")}
                  />
                  <input
                    value={leadDraft.phone}
                    onChange={(e) => setLeadDraft((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                    placeholder={t("conversations.leadData.phone")}
                  />
                  <input
                    value={leadDraft.email}
                    onChange={(e) => setLeadDraft((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                    placeholder={t("conversations.leadData.email")}
                  />
                  <select
                    value={leadDraft.status}
                    onChange={(e) =>
                      setLeadDraft((prev) => ({ ...prev, status: e.target.value as Lead["status"] }))
                    }
                    className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                  >
                    {LEAD_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={leadDraft.notes}
                    onChange={(e) => setLeadDraft((prev) => ({ ...prev, notes: e.target.value }))}
                    className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
                    rows={2}
                    placeholder={t("conversations.leadData.notes")}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={leadDraft.condition}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, condition: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.condition")}
                    />
                    <input
                      value={leadDraft.fuelType}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, fuelType: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.fuelType")}
                    />
                    <input
                      value={leadDraft.transmission}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, transmission: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.transmission")}
                    />
                    <input
                      value={leadDraft.minPrice}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, minPrice: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.minPrice")}
                    />
                    <input
                      value={leadDraft.maxPrice}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, maxPrice: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.maxPrice")}
                    />
                    <input
                      value={leadDraft.minYear}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, minYear: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.minYear")}
                    />
                    <input
                      value={leadDraft.maxYear}
                      onChange={(e) => setLeadDraft((prev) => ({ ...prev, maxYear: e.target.value }))}
                      className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
                      placeholder={t("conversations.leadData.maxYear")}
                    />
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={savingLead}
                    onClick={() => void persistLeadUpdate()}
                  >
                    {savingLead ? t("conversations.leadData.saving") : t("conversations.leadData.save")}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-2">{t("whatsapp.inbox.rightPanel.noLead")}</p>
            )}
          </div>
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="flex items-center justify-between gap-1 mb-2">
                <div className="flex items-center min-w-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 mr-1"
                    onClick={() => setIsFiltersSectionCollapsed((prev) => !prev)}
                    aria-label={
                      isFiltersSectionCollapsed
                        ? t("whatsapp.inbox.rightPanel.expandFiltersSection")
                        : t("whatsapp.inbox.rightPanel.collapseFiltersSection")
                    }
                    title={
                      isFiltersSectionCollapsed
                        ? t("whatsapp.inbox.rightPanel.expandFiltersSection")
                        : t("whatsapp.inbox.rightPanel.collapseFiltersSection")
                    }
                  >
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${
                        isFiltersSectionCollapsed ? "-rotate-90" : "rotate-0"
                      }`}
                    />
                  </Button>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-0">
                    {t("whatsapp.inbox.rightPanel.vehicleSearch")}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 shrink-0 text-xs"
                  disabled={vehiclesLoading || !phoneNumber}
                  onClick={() => void runVehicleSearch()}
                >
                  {vehiclesLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Search className="h-3.5 w-3.5 stroke-[1.5]" />
                  )}
                  {t("whatsapp.inbox.rightPanel.searchVehicles")}
                </Button>
              </div>
              <div
                className={`overflow-y-auto rounded-lg border border-border bg-muted/30 transition-all ${
                  isFiltersSectionCollapsed ? "max-h-0 opacity-0 border-transparent" : "max-h-[min(45vh,320px)] opacity-100"
                }`}
              >
                <VehicleFilters
                  filters={filters}
                  onFilterChange={setFilters}
                  onClearFilters={clearFilters}
                  activeCount={activeFilterCount}
                  brands={brands}
                  models={models}
                  versions={versions}
                  options={options}
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
              {phoneNumber && (
                <p className="text-[11px] text-muted-foreground px-0.5">
                  {t("whatsapp.inbox.rightPanel.vehiclesFound", { count: vehicleResultTotal })}
                </p>
              )}
              {vehiclesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : vehicles.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">{t("whatsapp.inbox.rightPanel.noVehicles")}</p>
              ) : (
                vehicles.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedVehicleId(v.id)}
                    className={`w-full text-left rounded-lg border p-2 transition-colors ${
                      selectedVehicleId === v.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex gap-2">
                      {v.photos?.[0]?.url ? (
                        <img
                          src={v.photos[0].url}
                          alt=""
                          className="w-14 h-14 rounded object-cover flex-shrink-0 bg-muted"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded bg-muted flex items-center justify-center flex-shrink-0">
                          <Car className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{vehicleTitle(v)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(
                            typeof v.salePrice === "string"
                              ? parseFloat(v.salePrice || "0")
                              : Number(v.salePrice || v.price || 0),
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {selectedVehicle && (
              <div className="p-3 border-t border-border space-y-2 bg-muted/20">
                <p className="text-xs font-medium text-foreground truncate">{vehicleTitle(selectedVehicle)}</p>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    size="sm"
                    className="w-full gap-2"
                    disabled={sendingText || !user}
                    onClick={() => void sendVehicleText()}
                  >
                    {sendingText ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                    {t("whatsapp.inbox.rightPanel.sendVehicleInfo")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full gap-2"
                    disabled={sendingPhotos}
                    onClick={() => setConfirmPhotosOpen(true)}
                  >
                    {sendingPhotos ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                    {t("whatsapp.inbox.rightPanel.sendPhotos")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full gap-2"
                    disabled={sendingDocs}
                    onClick={() => setConfirmDocsOpen(true)}
                  >
                    {sendingDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    {t("whatsapp.inbox.rightPanel.sendDocuments")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
